import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import PublicLayout from "../components/layout/PublicLayout";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { api, formatMoney } from "../lib/api";
import PriceDisplay from "../components/PriceDisplay";
import { useCurrency } from "../context/CurrencyContext";
import { carImageUrl, carImgFallback } from "../components/cars/CarCard";
import FilterBar from "../components/FilterBar";
import SearchBar from "../components/SearchBar";
import ShareReviewDialog from "../components/ShareReviewDialog";
import {
  Search, MapPin, ArrowRight, ShieldCheck, Wallet, Clock, CalendarX,
  Umbrella, LayoutGrid, CalendarCheck, Car as CarIcon, Star, Heart,
  PenSquare, Navigation,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1614638964097-20e7104dea3c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwxfHxkYXJrJTIwZm9yZXN0JTIwcm9hZHxlbnwwfHx8fDE3ODEwNDAyODJ8MA&ixlib=rb-4.1.0&q=85";

const CITY_IMGS = {
  Kigali: "https://images.unsplash.com/photo-1687986261123-b17f08f2796c?w=900&q=80",
  Musanze: "https://images.unsplash.com/photo-1644726367483-ee7418a19b7d?w=900&q=80",
  Huye: "https://images.unsplash.com/photo-1511284281977-10b7b4377cfc?w=900&q=80",
  Rubavu: "https://images.unsplash.com/photo-1626096687242-bbb8cb1ca031?w=900&q=80",
  Karongi: "https://images.unsplash.com/photo-1706977570024-fefa419c48c8?w=900&q=80",
};

const RWANDA_CITIES = [
  "Kigali","Musanze","Huye","Rubavu","Muhanga","Nyagatare","Rwamagana",
  "Rusizi","Kayonza","Kirehe","Bugesera","Gicumbi","Kamonyi","Ngoma",
  "Rulindo","Gakenke","Burera","Nyabihu","Ngororero","Rutsiro","Karongi",
  "Nyamasheke","Nyanza","Gisagara","Nyaruguru","Ruhango","Gatsibo",
  "Gitarama","Ruhengeri",
];
const FEATURED_CITIES = ["Kigali", "Musanze", "Rubavu", "Huye", "Karongi"];

function fuzzyMatch(query, city) {
  if (!query) return true;
  const q = query.toLowerCase().replace(/\s/g, "");
  const c = city.toLowerCase();
  if (c.includes(q)) return true;
  let qi = 0;
  for (let ci = 0; ci < c.length && qi < q.length; ci++) {
    if (c[ci] === q[qi]) qi++;
  }
  return qi === q.length;
}

// Generate time options 00:00 to 23:30 in 30-min steps
const TIME_OPTIONS = [];
for (let h = 0; h < 24; h++) {
  for (let m of [0, 30]) {
    const hh = String(h).padStart(2, "0");
    const mm = String(m).padStart(2, "0");
    const val = `${hh}:${mm}`;
    const ampm = h < 12 ? "AM" : "PM";
    const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
    TIME_OPTIONS.push({ val, label: `${displayH}:${mm} ${ampm}` });
  }
}

const AIRPORT_CODES = {
  Kigali: "Kigali International Airport (KGL)",
  Musanze: "Volcanoes National Park Area",
  Rubavu: "Gisenyi / Lake Kivu",
  Huye: "Butare / Southern Province",
  Nyagatare: "Eastern Province Gateway",
};

const FILTERS = [
  { key: "all", label: "All" },
  { key: "with_driver", label: "With Driver" },
  { key: "self_drive", label: "No Driver" },
  { key: "SUV", label: "SUV" },
  { key: "Sedan", label: "Sedan" },
  { key: "Pickup", label: "Pickup" },
  { key: "Hatchback", label: "Hatchback" },
  { key: "sponsored", label: "Sponsored ★" },
];

function SponsoredCard({ car }) {
  const { currencies } = useCurrency();
  return (
    <Link to={`/cars/${car.id}`}
      className="relative shrink-0 w-[200px] sm:w-[300px] lg:w-[280px] rounded-xl sm:rounded-2xl overflow-hidden bg-white shadow-organic hover:shadow-organic-hover transition-shadow group mx-1.5 sm:mx-2"
      data-testid={`sponsored-card-${car.id}`}>
      <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-[#E97A48] text-white text-[9px] sm:text-[10px] font-bold tracking-[0.14em] uppercase px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow-md">Sponsored</div>
      <div className="aspect-[16/10] overflow-hidden bg-muted">
        <img src={carImageUrl(car.images?.[0])} alt={car.title} onError={carImgFallback}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      </div>
      <div className="p-2.5 sm:p-4">
        <div className="flex items-start justify-between gap-1.5">
          <h3 className="font-display text-sm sm:text-lg font-bold tracking-tight truncate">{car.title}</h3>
          {car.rating > 0 && (
            <div className="flex items-center gap-1 text-[11px] sm:text-xs text-foreground/70 shrink-0">
              <Star size={10} className="fill-[#E97A48] text-[#E97A48]" />
              <span>{car.rating.toFixed(1)}</span>
              {(car.rating_count > 0 || car.review_count > 0) && <span className="text-muted-foreground">({car.rating_count ?? car.review_count ?? 0})</span>}
            </div>
          )}
        </div>
        <div className="mt-0.5 sm:mt-1 flex items-center gap-1 text-[11px] sm:text-xs text-muted-foreground">
          <MapPin size={10} className="text-[#2A9D8F]" /> {car.city}
        </div>
        <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
          {car.year && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.year}</span>}
          {car.seats && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.seats} seats</span>}
          {car.transmission && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.transmission === "Automatic" ? "Auto" : "Manual"}</span>}
          {car.fuel && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.fuel === "Plugin-Hybrid" ? "PHEV" : car.fuel === "Electric" ? "EV" : car.fuel}</span>}
        </div>
        <div className="mt-2 sm:mt-3 pt-2 sm:pt-3 border-t border-border flex items-baseline justify-between">
          <div className="text-[10px] sm:text-xs text-muted-foreground truncate capitalize">{car.car_type}</div>
          <div className="font-display font-bold text-[#2A9D8F] text-sm sm:text-base shrink-0">
            <PriceDisplay amount={car.price_per_day} listingCurrency={car.currency} /><span className="text-[10px] sm:text-xs font-normal text-muted-foreground"> /d</span>
          </div>
        </div>
      </div>
    </Link>
  );
}

function FeaturedCard({ car, idx }) {
  const { currencies } = useCurrency();
  return (
    <Link to={`/cars/${car.id}`} data-testid={`featured-card-${car.id}`} className="block fade-up" style={{ animationDelay: `${idx * 60}ms` }}>
      <Card className="relative overflow-hidden border-border bg-card hover:shadow-organic-hover transition-shadow group rounded-xl sm:rounded-2xl">
        {car.sponsored && (car.sponsored_paid || car.sponsored_admin_grant) && (
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 z-10 bg-[#E97A48] text-white text-[9px] sm:text-[10px] font-bold tracking-[0.14em] uppercase px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full shadow-md">Sponsored</div>
        )}
        <button type="button" aria-label="favorite" className="absolute top-2 right-2 sm:top-3 sm:right-3 z-10 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center text-muted-foreground hover:text-[#E97A48] transition-colors">
          <Heart size={12} className="sm:hidden" /><Heart size={14} className="hidden sm:block" />
        </button>
        <div className="aspect-[16/10] sm:aspect-[16/8] overflow-hidden bg-muted relative">
          <img src={carImageUrl(car.images?.[0])} alt={car.title} onError={carImgFallback}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        </div>
        <div className="p-2.5 sm:p-4">
          <div className="flex items-start justify-between gap-1.5">
            <h3 className="font-display text-sm sm:text-base font-bold tracking-tight truncate">{car.title}</h3>
            {car.rating > 0 && (
              <div className="flex items-center gap-1 text-[11px] sm:text-xs text-foreground/70 shrink-0">
                <Star size={10} className="fill-[#E97A48] text-[#E97A48]" />
                <span>{car.rating.toFixed(1)}</span>
                {(car.rating_count > 0 || car.review_count > 0) && <span className="text-muted-foreground">({car.rating_count ?? car.review_count ?? 0})</span>}
              </div>
            )}
          </div>
          <div className="mt-0.5 sm:mt-1 flex items-center gap-1 text-[11px] sm:text-xs text-muted-foreground">
            <MapPin size={10} className="text-[#2A9D8F]" /> <span className="truncate">{car.city}</span>
          </div>
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            {car.year && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.year}</span>}
            {car.seats && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.seats} seats</span>}
            {car.transmission && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.transmission === "Automatic" ? "Auto" : "Manual"}</span>}
            {car.fuel && <span className="text-[9px] sm:text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground">{car.fuel === "Plugin-Hybrid" ? "PHEV" : car.fuel === "Electric" ? "EV" : car.fuel}</span>}
          </div>
          <div className="mt-2.5 sm:mt-4 pt-2 sm:pt-3 border-t border-border flex items-center justify-between">
            <div>
              <div className="font-display text-sm sm:text-lg font-bold tracking-tight"><PriceDisplay amount={car.price_per_day} listingCurrency={car.currency} /></div>
              <div className="text-[10px] sm:text-xs text-muted-foreground -mt-0.5">per day</div>
            </div>
            <span className="px-2.5 sm:px-4 py-1 sm:py-1.5 rounded-full bg-[#2A9D8F] text-white text-[11px] sm:text-xs font-semibold tracking-wide group-hover:bg-[#23887A] transition-colors">Book</span>
          </div>
        </div>
      </Card>
    </Link>
  );
}

function HowItWorksStep({ icon: Icon, tileBg, iconColor, title, body, testId }) {
  return (
    <div className="flex flex-col items-center text-center gap-3" data-testid={testId}>
      <div className="w-16 h-16 rounded-2xl flex items-center justify-center" style={{ background: tileBg }}>
        <Icon size={26} style={{ color: iconColor }} />
      </div>
      <div>
        <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
        <p className="text-sm text-muted-foreground mt-2 max-w-xs">{body}</p>
      </div>
    </div>
  );
}

function BenefitCard({ icon: Icon, title, body, testId }) {
  return (
    <Card className="border-border p-6 rounded-2xl bg-card hover:shadow-organic transition-shadow" data-testid={testId}>
      <div className="w-12 h-12 rounded-xl bg-[#D0F5F2] flex items-center justify-center mb-4">
        <Icon size={22} className="text-[#2A9D8F]" />
      </div>
      <h3 className="font-display text-xl font-bold tracking-tight">{title}</h3>
      <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{body}</p>
    </Card>
  );
}

function ReviewCard({ review }) {
  const initials = (review.customer_name || "RX").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <div className="bg-white rounded-2xl shadow-organic p-6 w-[280px] sm:w-[320px] shrink-0 mx-3 flex flex-col" data-testid={`testimonial-card-${review.id}`}>
      <div className="flex items-center gap-1 mb-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star key={i} size={16} className={i < review.rating ? "fill-[#E97A48] text-[#E97A48]" : "text-[#EBE7E0]"} />
        ))}
      </div>
      <p className="font-display text-base text-foreground leading-snug line-clamp-5 flex-1">&ldquo;{review.comment}&rdquo;</p>
      <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
        <div className="w-11 h-11 rounded-full bg-[#D0F5F2] flex items-center justify-center font-display font-bold text-sm text-[#2A9D8F]">{initials}</div>
        <div>
          <div className="font-display font-bold text-sm tracking-tight">{review.customer_name}</div>
          <div className="text-xs text-muted-foreground">{review.location || "Rwanda"}</div>
        </div>
      </div>
    </div>
  );
}

function CityCard({ name, count, idx }) {
  return (
    <Link to={`/cars?city=${encodeURIComponent(name)}`}
      className="relative aspect-[4/5] sm:aspect-[3/4] rounded-2xl overflow-hidden group fade-up shadow-organic"
      style={{ animationDelay: `${idx * 60}ms` }} data-testid={`city-card-${name}`}>
      <img src={CITY_IMGS[name] || "https://images.unsplash.com/photo-1535320394131-79a01a25dd71?w=900&q=80"}
        alt={name} onError={carImgFallback}
        className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
      <div className="absolute bottom-4 left-4 right-4 text-white">
        <div className="font-display text-2xl font-bold tracking-tight">{name}</div>
        <div className="text-xs opacity-90 mt-0.5">{count} car{count === 1 ? "" : "s"}</div>
      </div>
    </Link>
  );
}

function todayStr() {
  const d = new Date();
  return d.toISOString().split("T")[0];
}

function formatDisplayDate(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  const months = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${months[parseInt(m)-1]} ${parseInt(d)}, ${y}`;
}

function formatTime(val) {
  if (!val) return "10:00 AM";
  const [h, m] = val.split(":").map(Number);
  const ampm = h < 12 ? "AM" : "PM";
  const dh = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${dh}:${String(m).padStart(2,"0")} ${ampm}`;
}

function MiniCalendar({ value, onChange, minDate }) {
  const today = new Date();
  const initDate = value ? new Date(value + "T00:00:00") : today;
  const [viewYear, setViewYear] = React.useState(initDate.getFullYear());
  const [viewMonth, setViewMonth] = React.useState(initDate.getMonth());
  const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
  const DAYS = ["Su","Mo","Tu","We","Th","Fr","Sa"];
  const firstDay = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const minD = minDate ? new Date(minDate + "T00:00:00") : null;
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  const selD = value ? new Date(value + "T00:00:00") : null;
  const isSelected = (d) => selD && selD.getFullYear() === viewYear && selD.getMonth() === viewMonth && selD.getDate() === d;
  const isDisabled = (d) => { if (!minD) return false; const cd = new Date(viewYear, viewMonth, d); return cd < minD; };
  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y-1); } else setViewMonth(m => m-1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y+1); } else setViewMonth(m => m+1); };
  const pick = (d) => {
    if (isDisabled(d)) return;
    const mm = String(viewMonth+1).padStart(2,"0");
    const dd = String(d).padStart(2,"0");
    onChange(`${viewYear}-${mm}-${dd}`);
  };
  return (
    <div className="w-full select-none">
      <div className="flex items-center justify-between mb-1.5">
        <button type="button" onClick={prevMonth} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-muted text-sm font-bold">&#8249;</button>
        <span className="text-xs font-semibold">{MONTHS[viewMonth]} {viewYear}</span>
        <button type="button" onClick={nextMonth} className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-muted text-sm font-bold">&#8250;</button>
      </div>
      <div className="grid grid-cols-7 gap-0 mb-0.5">
        {DAYS.map(d => <div key={d} className="text-center text-[9px] font-bold text-muted-foreground py-0.5">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-0">
        {cells.map((d, i) => d === null ? <div key={i} /> : (
          <button key={i} type="button" onClick={() => pick(d)}
            disabled={isDisabled(d)}
            className={`w-full aspect-square flex items-center justify-center rounded-full text-[11px] transition-colors
              ${isSelected(d) ? "bg-[#2A9D8F] text-white font-bold" : ""}
              ${!isSelected(d) && !isDisabled(d) ? "hover:bg-[#D0F5F2] text-foreground" : ""}
              ${isDisabled(d) ? "text-muted-foreground/30 cursor-not-allowed" : "cursor-pointer"}`}>
            {d}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function Landing() {
  const navigate = useNavigate();
  const [sponsored, setSponsored] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityCounts, setCityCounts] = useState({});
  const [filter, setFilter] = useState("all");
  const [landingFilters, setLandingFilters] = React.useState({ q: "", city: "", country_code: "", transmission: "", fuel: "", seats: "", price: [0, 300000], provides_driver: "", sponsored: "", car_type: "", category: "" });
  const [landingSortBy, setLandingSortBy] = React.useState("default");
  const [showShareReview, setShowShareReview] = useState(false);

  // Search modal
  const [searchWhere, setSearchWhere] = useState("");
  const [searchFrom, setSearchFrom] = useState("");
  const [searchFromTime, setSearchFromTime] = useState("10:00");
  const [searchUntil, setSearchUntil] = useState("");
  const [searchUntilTime, setSearchUntilTime] = useState("10:00");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeField, setActiveField] = useState(null);
  const [locating, setLocating] = useState(false);

  const fromRef = useRef(null);
  const fromTimeRef = useRef(null);
  const untilRef = useRef(null);
  const untilTimeRef = useRef(null);
  const searchBtnRef = useRef(null);


  useEffect(() => {
    api.get("/cars/sponsored/list", { params: { limit: 12 } })
      .then((res) => setSponsored(res.data)).catch(() => setSponsored([]));
    api.get("/reviews/testimonials", { params: { limit: 8 } })
      .then((res) => setReviews(res.data)).catch(() => setReviews([]));
    api.get("/cars", { params: { limit: 100 } })
      .then(({ data }) => {
        const counts = {};
        data.forEach((c) => {
          const raw = (c.city || "").trim();
          const matched = FEATURED_CITIES.find((f) => f.toLowerCase() === raw.toLowerCase());
          const key = matched || raw;
          counts[key] = (counts[key] || 0) + 1;
        });
        setCityCounts(counts);
        setCities(FEATURED_CITIES.map((name) => ({ name, count: counts[name] || 0 })));
      }).catch(() => setCities(FEATURED_CITIES.map((name) => ({ name, count: 0 }))));
  }, []);

  useEffect(() => {
    const params = {};
    // pill filter (legacy — kept for filterQuery compatibility)
    if (filter === "with_driver") params.provides_driver = true;
    else if (filter === "self_drive") params.provides_driver = false;
    else if (filter === "sponsored") params.sponsored = true;
    else if (filter !== "all") params.car_type = filter;
    // FilterBar selections override pill filter
    if (landingFilters.provides_driver === "true") params.provides_driver = true;
    else if (landingFilters.provides_driver === "false") params.provides_driver = false;
    if (landingFilters.sponsored === "true") params.sponsored = true;
    if (landingFilters.car_type) params.car_type = landingFilters.car_type;
    if (landingFilters.category) params.category = landingFilters.category;
    if (landingFilters.q) params.q = landingFilters.q;
    if (landingFilters.city) params.city = landingFilters.city;
    if (landingFilters.country_code) params.country_code = landingFilters.country_code;
    if (landingFilters.transmission) params.transmission = landingFilters.transmission;
    if (landingFilters.fuel) params.fuel = landingFilters.fuel;
    if (landingFilters.seats) params.seats = landingFilters.seats;
    if (landingFilters.price[0] > 0) params.min_price = landingFilters.price[0];
    if (landingFilters.price[1] < 300000) params.max_price = landingFilters.price[1];
    api.get("/cars", { params: { ...params, limit: 100 } }).then((res) => {
      let results = res.data;
      if (landingSortBy === "price_asc") results = [...results].sort((a,b) => a.price_per_day - b.price_per_day);
      else if (landingSortBy === "price_desc") results = [...results].sort((a,b) => b.price_per_day - a.price_per_day);
      else if (landingSortBy === "rating") results = [...results].sort((a,b) => (b.rating||0)-(a.rating||0));
      setFeatured(results);
    }).catch(() => setFeatured([]));
  }, [filter, landingFilters, landingSortBy]);

  const filterQuery = (() => {
    const p = new URLSearchParams();
    if (landingFilters.provides_driver === "true") p.set("provides_driver", "true");
    else if (landingFilters.provides_driver === "false") p.set("provides_driver", "false");
    else if (filter === "with_driver") p.set("provides_driver", "true");
    else if (filter === "self_drive") p.set("provides_driver", "false");
    if (landingFilters.sponsored === "true" || filter === "sponsored") p.set("sponsored", "true");
    if (landingFilters.car_type) p.set("car_type", landingFilters.car_type);
    if (landingFilters.category) p.set("category", landingFilters.category);
    else if (filter !== "all" && filter !== "with_driver" && filter !== "self_drive" && filter !== "sponsored") p.set("car_type", filter);
    if (landingFilters.q) p.set("q", landingFilters.q);
    if (landingFilters.city) p.set("q", landingFilters.city);
    if (landingFilters.country_code) p.set("country_code", landingFilters.country_code);
    if (landingFilters.transmission) p.set("transmission", landingFilters.transmission);
    if (landingFilters.fuel) p.set("fuel", landingFilters.fuel);
    if (landingFilters.seats) p.set("seats", landingFilters.seats);
    if (landingFilters.price && landingFilters.price[0] > 0) p.set("min_price", landingFilters.price[0]);
    if (landingFilters.price && landingFilters.price[1] < 300000) p.set("max_price", landingFilters.price[1]);
    const qs = p.toString();
    return qs ? "?" + qs : "";
  })();

  const handleCitySelect = (city) => {
    setSearchWhere(city);
    setShowSuggestions(false);
    setTimeout(() => { try { fromRef.current?.focus(); } catch(e) {} }, 150);
  };

  const handleFromChange = (e) => {
    setSearchFrom(e.target.value);
    
  };

  const handleUntilChange = (e) => {
    setSearchUntil(e.target.value);
    if (e.target.value) setTimeout(() => { try { searchBtnRef.current?.focus(); } catch(e) {} }, 150);
  };

  const handleCurrentLocation = async () => {
    setLocating(true);
    try {
      const res = await fetch('https://api.bigdatacloud.net/data/reverse-geocode-client?localityLanguage=en');
      const data = await res.json();
      const city = data.city || data.locality || data.principalSubdivision || 'Kigali';
      handleCitySelect(city);
    } catch(e) {
      handleCitySelect('Kigali');
    } finally {
      setLocating(false);
    }
  }


  const handleSearch = () => {
    const params = new URLSearchParams();
    if (searchWhere) params.set("q", searchWhere);
    if (searchFrom) params.set("from", `${searchFrom}T${searchFromTime}`);
    if (searchUntil) params.set("until", `${searchUntil}T${searchUntilTime}`);
    navigate(`/cars?${params.toString()}`);
  };

  const allCities = [...new Set([...Object.keys(cityCounts), ...RWANDA_CITIES])];
  const suggestions = allCities
    .filter(city => fuzzyMatch(searchWhere, city))
    .sort((a, b) => (cityCounts[b] || 0) - (cityCounts[a] || 0))
    .slice(0, 8);

  const readyToSearch = searchWhere && searchFrom && searchUntil;
  // Marquee needs enough repeated content to stay full-width on very wide
  // desktop screens even when there are only a few real items — repeat
  // more aggressively the fewer items there are.
  const sponsoredRepeats = sponsored.length > 0 ? Math.max(2, Math.ceil(12 / sponsored.length)) : 0;
  const sponsoredLoop = sponsoredRepeats > 0 ? Array(sponsoredRepeats).fill(sponsored).flat() : [];
  const reviewsRepeats = reviews.length > 0 ? Math.max(2, Math.ceil(12 / reviews.length)) : 0;
  const reviewsLoop = reviewsRepeats > 0 ? Array(reviewsRepeats).fill(reviews).flat() : [];

  return (
    <PublicLayout>
      {/* HERO */}
      <section
        className="relative bg-cover bg-center min-h-[280px] sm:min-h-[360px] flex items-center"
        style={{ backgroundImage: `linear-gradient(to bottom, rgba(11,25,20,0.55), rgba(11,25,20,0.7)), url(${HERO_IMG})` }}
        data-testid="hero-section"
      >
        <div className="mx-auto max-w-2xl px-4 sm:px-6 w-full py-6 sm:py-10">
          <div className="text-center mb-4 sm:mb-6">
            <h1 className="font-display text-2xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white">Car Rental Reinvented</h1>
            <p className="hidden sm:block mt-3 sm:mt-5 text-sm sm:text-lg text-white/90 leading-relaxed max-w-2xl mx-auto">
              Rent from verified hosts in Rwanda. Skip the rental counter and find the perfect vehicle for your journey.
            </p>
          </div>

          <SearchBar showTrustSignals={true} initialWhere={searchWhere} />
        </div>
      </section>

      {/* FILTER PILLS */}
      <section className="bg-background pt-2 sm:pt-3" data-testid="filter-pills-section">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 pb-2">
          <FilterBar
            filters={landingFilters}
            setFilters={setLandingFilters}
            onApply={(f) => setLandingFilters(f)}
            sortBy={landingSortBy}
            setSortBy={setLandingSortBy}
            pillBg="bg-[#EBE7E0] text-[#4A5D54] hover:bg-[#E0DCD3]"
            pillActiveBg="bg-[#2A9D8F] text-white"
          />
        </div>
      </section>

      {/* SPONSORED */}
      {sponsoredLoop.length > 0 && (
        <section className="relative overflow-hidden" data-testid="sponsored-section">
          <div className="marquee-track flex" style={{ "--marquee-duration": `${Math.max(sponsored.length * 6, 30)}s`, "--marquee-translate": `${-100 / sponsoredRepeats}%` }}>
            {sponsoredLoop.map((car, i) => <SponsoredCard key={`${car.id}-${i}`} car={car} />)}
          </div>
        </section>
      )}

      {/* FEATURED CARS */}
      <section className="bg-background pt-4 pb-10 sm:pt-8 sm:pb-20" data-testid="featured-cars-section">
        <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8">
          <div className="mb-3 sm:mb-6">
            <h2 className="font-display text-xl sm:text-4xl font-bold tracking-tight">
              {filter === "all" ? "Featured Cars" : (FILTERS.find(f => f.key === filter)?.label || "Cars")}
            </h2>
            <p className="text-xs sm:text-base text-muted-foreground mt-0.5 sm:mt-1">
              {featured.length} matching {featured.length === 1 ? "car" : "cars"} — swipe to see more
            </p>
          </div>
          {featured.length === 0 ? (
            <div className="text-center text-muted-foreground py-10 border border-dashed border-border rounded-2xl text-sm">No cars match this filter yet.</div>
          ) : (
            <div className="flex gap-3 sm:gap-5 overflow-x-auto pb-3 -mx-3 px-3 sm:-mx-6 sm:px-6 scrollbar-hide snap-x snap-mandatory">
              {featured.map((car, idx) => (
                <div key={car.id} className="shrink-0 w-[200px] sm:w-[280px] snap-start">
                  <FeaturedCard car={car} idx={idx} />
                </div>
              ))}
            </div>
          )}
          <div className="mt-6 sm:mt-8 text-center">
            <Button asChild className="bg-[#2A9D8F] hover:bg-[#23887A] rounded-full px-6 h-11">
              <Link to={`/cars${filterQuery}`}>See all cars <ArrowRight size={14} className="ml-1.5" /></Link>
            </Button>
          </div>
        </div>
      </section>

      {/* LIST YOUR CAR */}
      <section className="pt-6 pb-10 sm:pt-12 sm:pb-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Link to="/list-your-car"
            className="block bg-[#2A9D8F] hover:bg-[#23887A] transition-colors rounded-2xl sm:rounded-3xl px-5 sm:px-12 py-4 sm:py-8 text-center text-white shadow-organic">
            <div className="font-display text-base sm:text-3xl font-bold tracking-tight inline-flex items-center gap-2 sm:gap-3 flex-wrap justify-center">
              List Your Car — Start Earning
              <ArrowRight size={16} className="sm:hidden" /><ArrowRight size={24} className="hidden sm:block" />
            </div>
          </Link>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-12 sm:py-16" data-testid="how-it-works-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">How it works</h2>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto">Renting a car has never been easier.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 sm:gap-8">
            <HowItWorksStep icon={Search} tileBg="#D6EAF8" iconColor="#2E6FB5" title="Find the perfect car" body="Enter your location and dates to browse cars shared by local hosts." testId="how-it-works-step-1" />
            <HowItWorksStep icon={CalendarCheck} tileBg="#D0F5F2" iconColor="#2A9D8F" title="Book instantly" body="Choose your car, add insurance if needed, and book securely." testId="how-it-works-step-2" />
            <HowItWorksStep icon={CarIcon} tileBg="#FADBD8" iconColor="#C05A3A" title="Pick up and drive" body="Meet the host, grab the keys, and hit the road with 24/7 support." testId="how-it-works-step-3" />
            <HowItWorksStep icon={Star} tileBg="#FCF3CF" iconColor="#C8A93C" title="Return and rate" body="Return the car, leave a review, and plan your next adventure." testId="how-it-works-step-4" />
          </div>
        </div>
      </section>

      {/* WHY CHOOSE */}
      <section className="bg-background py-16 sm:py-20" data-testid="why-choose-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Why choose RydeXpress</h2>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto">Seamless, secure, and affordable car sharing.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
            <BenefitCard icon={ShieldCheck} title="Verified Hosts" body="Every host undergoes strict verification for your safety." testId="benefit-verified-hosts" />
            <BenefitCard icon={Wallet} title="Affordable Prices" body="Competitive rates directly from local car owners." testId="benefit-affordable" />
            <BenefitCard icon={Clock} title="24/7 Support" body="Our team is available around the clock to assist you." testId="benefit-support" />
            <BenefitCard icon={CalendarX} title="Flexible Cancellation" body="The booking fee is non-refundable, but if plans change, tell us why — our team reviews every request and may issue a refund." testId="benefit-cancellation" />
            <BenefitCard icon={Umbrella} title="Insurance Included" body="Every rental includes comprehensive insurance coverage options." testId="benefit-insurance" />
            <BenefitCard icon={LayoutGrid} title="Wide Selection" body="From compact city cars to spacious SUVs for any occasion." testId="benefit-selection" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      {reviewsLoop.length > 0 && (
        <section className="bg-[#2A9D8F] py-12 sm:py-20 overflow-hidden" data-testid="testimonials-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 mb-8 sm:mb-10 text-center">
            <h2 className="font-display text-2xl sm:text-4xl font-bold tracking-tight text-white">What our customers say</h2>
            <p className="text-white/90 mt-2 max-w-2xl mx-auto text-sm sm:text-base">Join thousands of happy drivers who discovered a better way to rent cars.</p>
            <div className="mt-5 sm:mt-6">
              <Button onClick={() => setShowShareReview(true)}
                className="bg-[#E97A48] hover:bg-[#D86A38] text-white rounded-full h-11 sm:h-12 px-6 sm:px-7 btn-lift">
                <PenSquare size={16} className="mr-2" /> Share Your Experience
              </Button>
            </div>
          </div>
          <div className="marquee-track flex" style={{ "--marquee-duration": `${Math.max(reviews.length * 7, 35)}s`, "--marquee-translate": `${-100 / reviewsRepeats}%` }}>
            {reviewsLoop.map((r, i) => <ReviewCard key={`${r.id}-${i}`} review={r} />)}
          </div>
        </section>
      )}

      <ShareReviewDialog open={showShareReview} onOpenChange={setShowShareReview} />

      {/* POPULAR CITIES */}
      {cities.length > 0 && (
        <section className="py-16 sm:py-20" data-testid="popular-cities-section">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mb-10">
              <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Popular cities in Rwanda</h2>
              <p className="text-muted-foreground mt-2">Find the perfect ride for your next destination.</p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-5">
              {cities.map((c, i) => <CityCard key={c.name} name={c.name} count={c.count} idx={i} />)}
            </div>
          </div>
        </section>
      )}
    </PublicLayout>
  );
}
